import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { downloadBlob } from '../../lib/download'
import InventoryAtPointSummary from '../../components/InventoryAtPointSummary'
import ShiftBikesSnapshot, { type BikesSnapshot } from '../../components/ShiftBikesSnapshot'
import { formatTenantDateTime } from '../../lib/dates'
import ManualShiftModal from './ManualShiftModal'
// Note: forceClose removed — owner changes manager password instead

interface ShiftRevenue { cash: number; card: number; transfer: number; unknown: number; total: number }
interface Cashout { id: string; amount: string; note?: string; createdAt: string }
interface PaymentLine { type: string; bikeCodes: string; amount: number; paymentMethod: string | null }
interface ActiveDailyRental { bikeCode: string; customerName: string | null; plannedReturnAt: string | null; basePrice: number }
interface ShiftItem {
  id: string; status: 'open' | 'closed'
  source?: 'auto' | 'manual'
  notes?: string | null
  openedAt: string; closedAt?: string
  manager: { id: string; name: string; phone: string }
  point: { id: string; name: string }
  rentalCount: number
  activeDailyCount?: number
  activeDailyRentals?: ActiveDailyRental[]
  revenue: ShiftRevenue
  cashBalance: number
  cashouts: Cashout[]
  paymentLines: PaymentLine[]
  bikesSnapshot?: BikesSnapshot | null
}

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  prepayment: 'Предоплата',
  topup: 'Оплата',
  refund: 'Возврат',
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  cash: '💵 Нал',
  card: '💳 Безнал',
  transfer: '📲 Перевод',
}

function fmt(n: number) {
  return n.toLocaleString('ru-RU') + ' ₽'
}

function fmtTime(iso: string) {
  return formatTenantDateTime(iso)
}

export default function ShiftsPage() {
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const statusFromUrl = searchParams.get('status')
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>(
    statusFromUrl === 'open' || statusFromUrl === 'closed' ? statusFromUrl : 'all'
  )
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [cashoutShiftId, setCashoutShiftId] = useState<string | null>(null)
  const [cashoutAmount, setCashoutAmount] = useState('')
  const [cashoutNote, setCashoutNote] = useState('')
  const [exportingCsv, setExportingCsv] = useState(false)
  const [manualOpen, setManualOpen] = useState(false)
  const [manualOnly, setManualOnly] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['shifts', statusFilter, from, to, manualOnly],
    queryFn: () => api.get('/shifts', {
      params: {
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(manualOnly ? { source: 'manual' } : {}),
        from: new Date(from).toISOString(),
        to: new Date(to + 'T23:59:59').toISOString(),
        limit: 200,
      },
    }).then((r) => r.data),
    staleTime: statusFilter !== 'closed' ? 30_000 : 2 * 60_000,
    refetchInterval: statusFilter !== 'closed' ? 60_000 : false,
  })

  const shifts: ShiftItem[] = data?.shifts ?? []
  const expandedShift = expandedId ? shifts.find((s) => s.id === expandedId) : undefined

  // Live inventory is only needed when there's no stored snapshot (open shift, or an
  // old closed shift from before snapshots). A closed shift with a snapshot skips it.
  const needsLiveInventory = Boolean(expandedShift?.point.id) &&
    !(expandedShift?.status === 'closed' && expandedShift?.bikesSnapshot)
  const { data: bikesAtPointData, isLoading: bikesAtPointLoading } = useQuery({
    queryKey: ['owner-shift-point-bikes', expandedShift?.point.id],
    queryFn: () => api.get('/bikes', { params: { pointId: expandedShift!.point.id } }).then((r) => r.data),
    enabled: needsLiveInventory,
  })

  const cashoutMut = useMutation({
    mutationFn: ({ shiftId, amount, note }: { shiftId: string; amount: number; note?: string }) =>
      api.post(`/shifts/${shiftId}/cashout`, { amount, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] })
      setCashoutShiftId(null)
      setCashoutAmount('')
      setCashoutNote('')
    },
  })

  const handleCashout = () => {
    const amt = parseFloat(cashoutAmount.replace(',', '.'))
    if (!cashoutShiftId || isNaN(amt)) return
    cashoutMut.mutate({ shiftId: cashoutShiftId, amount: amt, note: cashoutNote || undefined })
  }

  const handleExportCsv = async () => {
    setExportingCsv(true)
    try {
      const res = await api.get('/shifts/csv', {
        responseType: 'blob',
        params: { from: new Date(from).toISOString(), to: new Date(to + 'T23:59:59').toISOString() },
      })
      await downloadBlob(res.data as Blob, `shifts-${from}-${to}.csv`)
    } catch { alert('Ошибка экспорта') }
    finally { setExportingCsv(false) }
  }

  const openCount = shifts.filter((s) => s.status === 'open').length

  return (
    <div className="p-4 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Смены</h2>
          {openCount > 0 && (
            <p className="text-sm text-green-600 font-medium">● {openCount} открыта{openCount > 1 ? '' : ''}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setManualOpen(true)}
            className="text-sm border border-amber-300 text-amber-700 px-3 py-2 rounded-xl hover:bg-amber-50"
          >
            📝 Ручная
          </button>
          <button
            onClick={handleExportCsv}
            disabled={exportingCsv}
            className="text-sm border border-gray-300 text-gray-600 px-3 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-60"
          >
            {exportingCsv ? 'Экспорт...' : '📥 CSV'}
          </button>
        </div>
      </div>

      <ManualShiftModal open={manualOpen} onClose={() => setManualOpen(false)} />

      {/* Filters */}
      <div className="bg-white rounded-2xl p-3 mb-4 space-y-3">
        <div className="flex gap-2">
          {(['all', 'open', 'closed'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${statusFilter === s ? 'bg-brand-600 text-white' : 'border border-gray-200 text-gray-600'}`}
            >
              {s === 'all' ? 'Все' : s === 'open' ? '🟢 Открытые' : '⚫ Закрытые'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">С</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">По</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={manualOnly} onChange={(e) => setManualOnly(e.target.checked)} />
          📝 Только ручные смены
        </label>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="bg-gray-100 rounded-2xl h-24 animate-pulse" />)}
        </div>
      )}

      {!isLoading && !shifts.length && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">📋</div>
          <p>Смен не найдено</p>
        </div>
      )}

      <div className="space-y-3">
        {shifts.map((s) => {
          const isExpanded = expandedId === s.id
          const duration = s.closedAt
            ? Math.round((new Date(s.closedAt).getTime() - new Date(s.openedAt).getTime()) / 60000)
            : Math.round((Date.now() - new Date(s.openedAt).getTime()) / 60000)
          const durationStr = duration < 60 ? `${duration} мин` : `${Math.floor(duration / 60)} ч ${duration % 60 > 0 ? duration % 60 + ' мин' : ''}`

          return (
            <div key={s.id} className={`bg-white rounded-2xl border transition-colors ${s.status === 'open' ? 'border-green-200' : 'border-gray-200'}`}>
              {/* Header */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : s.id)}
                className="w-full p-4 text-left"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`w-2 h-2 rounded-full ${s.status === 'open' ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <span className="font-semibold text-sm">{s.point.name}</span>
                      {s.source === 'manual' && (
                        <span
                          title={s.notes ?? 'Создана владельцем вручную'}
                          className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium"
                        >📝 ручная</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">{s.manager.name}</p>
                    <p className="text-xs text-gray-400">{s.manager.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-700">{fmt(s.revenue.total)}</p>
                    <p className="text-xs text-gray-400">
                      {s.rentalCount} аренд
                      {(s.activeDailyCount ?? 0) > 0 && (
                        <span className="ml-1 text-amber-600">({s.activeDailyCount} сут. активны)</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400">{durationStr}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-xs text-gray-400">Открыта: {fmtTime(s.openedAt)}</p>
                  {s.closedAt && <p className="text-xs text-gray-400">→ {fmtTime(s.closedAt)}</p>}
                </div>
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-4 space-y-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Инвентарь на точке</p>
                    {s.status === 'closed' && s.bikesSnapshot ? (
                      // Closed shift with a snapshot → show what was there at close time.
                      <ShiftBikesSnapshot snapshot={s.bikesSnapshot} />
                    ) : bikesAtPointLoading ? (
                      <p className="text-xs text-gray-400">Загрузка...</p>
                    ) : (
                      <>
                        <InventoryAtPointSummary bikes={bikesAtPointData?.bikes ?? []} />
                        {s.status === 'closed' && (
                          // Old shift closed before snapshots existed — be honest it's live.
                          <p className="text-[11px] text-amber-600 mt-1">⚠️ Текущее наличие (снимок на момент этой смены не сохранён)</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Active daily rentals that were still running when shift closed */}
                  {(s.activeDailyRentals ?? []).length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-xs font-semibold text-amber-700 uppercase mb-2">
                        Суточные аренды (активны во время смены)
                      </p>
                      <div className="space-y-1">
                        {(s.activeDailyRentals ?? []).map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-amber-800 font-medium">{r.bikeCode}</span>
                            {r.customerName && <span className="text-amber-700">{r.customerName}</span>}
                            <span className="text-amber-600">
                              {r.plannedReturnAt
                                ? `до ${formatTenantDateTime(r.plannedReturnAt)}`
                                : ''}
                            </span>
                            <span className="text-amber-800 font-semibold">{r.basePrice.toLocaleString('ru-RU')} ₽</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Revenue breakdown */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Выручка по типам</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: '💵 Наличные', value: s.revenue.cash },
                        { label: '💳 Безнал', value: s.revenue.card },
                        { label: '📲 Перевод', value: s.revenue.transfer },
                      ].map((item) => (
                        <div key={item.label} className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-gray-500">{item.label}</p>
                          <p className="font-bold text-sm mt-1">{fmt(item.value)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex justify-between items-center bg-green-50 rounded-xl px-4 py-2">
                      <span className="text-sm text-gray-600">Итого</span>
                      <span className="font-bold text-green-700">{fmt(s.revenue.total)}</span>
                    </div>
                  </div>

                  {/* Payment lines summary */}
                  {(s.paymentLines ?? []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Сводка операций</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-400">
                            <th className="text-left pb-1">Операция</th>
                            <th className="text-left pb-1">Инвентарь</th>
                            <th className="text-left pb-1">Способ</th>
                            <th className="text-right pb-1">Сумма</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {s.paymentLines.map((pl, i) => (
                            <tr key={i}>
                              <td className="py-0.5 text-gray-600">{PAYMENT_TYPE_LABEL[pl.type] ?? pl.type}</td>
                              <td className="py-0.5 text-gray-500">#{pl.bikeCodes}</td>
                              <td className="py-0.5 text-gray-500">{pl.paymentMethod ? (PAYMENT_METHOD_LABEL[pl.paymentMethod] ?? pl.paymentMethod) : '—'}</td>
                              <td className={`py-0.5 text-right font-semibold ${pl.amount < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                                {pl.amount < 0 ? `−${fmt(-pl.amount)}` : fmt(pl.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Cash balance */}
                  <div className="flex justify-between items-center bg-amber-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Наличные в кассе</p>
                      <p className="text-xs text-amber-600">Наличная выручка − инкассации</p>
                    </div>
                    <span className={`text-lg font-bold ${s.cashBalance >= 0 ? 'text-amber-700' : 'text-red-600'}`}>
                      {fmt(s.cashBalance)}
                    </span>
                  </div>

                  {/* Cashouts history */}
                  {s.cashouts.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Инкассации</p>
                      <div className="space-y-1">
                        {s.cashouts.map((c) => (
                          <div key={c.id} className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 text-xs">{fmtTime(c.createdAt)}{c.note ? ` · ${c.note}` : ''}</span>
                            <span className={`font-semibold ${Number(c.amount) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {Number(c.amount) > 0 ? '−' : '+'}{fmt(Math.abs(Number(c.amount)))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hint for open shifts instead of force-close */}
                  {s.status === 'open' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-500">
                      💡 Если нужно закрыть смену вместо менеджера — смените его пароль в разделе <strong>Менеджеры</strong>, войдите под его учётной записью и закройте смену.
                    </div>
                  )}

                  {/* Cashout button — only for open shifts */}
                  {s.status === 'open' && cashoutShiftId === s.id ? (
                    <div className="space-y-2 border-t pt-3">
                      <p className="text-sm font-semibold">Инкассация</p>
                      <input
                        type="number"
                        value={cashoutAmount}
                        onChange={(e) => setCashoutAmount(e.target.value)}
                        placeholder="Сумма (отрицательная = пополнение кассы)"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm"
                      />
                      <input
                        value={cashoutNote}
                        onChange={(e) => setCashoutNote(e.target.value)}
                        placeholder="Комментарий (необязательно)"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm"
                      />
                      <p className="text-xs text-gray-400">Отрицательная сумма означает, что владелец добавил деньги в кассу</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCashout}
                          disabled={cashoutMut.isPending || !cashoutAmount}
                          className="flex-1 bg-brand-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                        >
                          {cashoutMut.isPending ? 'Сохранение...' : 'Сохранить'}
                        </button>
                        <button
                          onClick={() => setCashoutShiftId(null)}
                          className="flex-1 border border-gray-200 py-2.5 rounded-xl text-sm"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : s.status === 'open' ? (
                    <button
                      onClick={() => { setCashoutShiftId(s.id); setCashoutAmount(''); setCashoutNote('') }}
                      className="w-full border border-amber-300 text-amber-700 py-2.5 rounded-xl text-sm font-medium hover:bg-amber-50"
                    >
                      💰 Инкассация
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
